import { getIndice } from './coupDePouce';
import { payloadsDuBloc } from './evalPapier';

/**
 * Planche de coups de pouce à découper (éval papier, décision prof 16/09).
 *
 * Le prof imprime son sujet ; en queue de document, une ou plusieurs feuilles
 * de cartes. Il les découpe, les pose sur son bureau, et donne la carte de
 * l'exercice 3 à l'élève qui bloque sur l'exercice 3. La carte n'est JAMAIS
 * sur la feuille de l'exercice : sinon l'élève la lit avant d'essayer, et ce
 * n'est plus une aide, c'est le cours.
 *
 * Deux règles que l'inventaire du 17/09 a imposées :
 *
 * 1. FILTRE MÉCANIQUE. Nos fiches sont dessinées pour un écran et beaucoup
 *    bougent : elles se construisent au clic, ou étape par étape sous le
 *    pilotage du CSS de la feuille élève (`CSS_PAS_A_PAS`). Sur papier il n'y a
 *    ni clic ni temps qui passe : la figure sort vide, ou tout se superpose.
 *    Une page portant l'un des marqueurs ci-dessous ne devient pas une carte.
 *    Chiffres du parc : 490 pages imprimables sur 821, 100 attendus
 *    entièrement couverts et 67 partiellement sur 235.
 *
 * 2. DÉDOUBLONNAGE PAR CONTENU, jamais par identité d'objet. Les fiches ne
 *    sont partagées entre formats que dans les lots récents : 1080 entrées de
 *    registre pour 491 aides réellement distinctes. Sans ça, une évaluation
 *    portant deux formats du même attendu imprimerait deux fois la même carte.
 */

/** Ce qui rend une page non imprimable. Une seule occurrence suffit. */
export const MOTIFS_NON_IMPRIMABLES = [
    // SMIL. L'état imprimé est le temps zéro, et une soixantaine de fiches ne
    // révèlent leur contenu qu'au clic d'un bouton dessiné dans le SVG.
    /<animate|<animateTransform|<animateMotion|<set\b/,
    // Pas à pas : le cumul des étapes est piloté par le CSS de la feuille
    // élève, absent du papier. Sans lui, les étapes se superposent.
    /data-etapes/,
    // Frame indéterminée. Et surtout : un <style> injecté en ligne n'est pas
    // encapsulé, il restylerait toute la feuille A4 (les fiches déclarent des
    // sélecteurs courts et non préfixés du genre .anim, .c1, .d1).
    /@keyframes|<style/,
    // Contenu masqué en attendant sa révélation. Ne doit pas attraper "0.5" :
    // le guillemet fermant est exigé.
    /opacity\s*=\s*"0"|opacity\s*:\s*0\s*[;"'}]/,
    /<script/,
];

/** Une page de fiche s'imprime-t-elle telle quelle ? */
export const pageImprimable = (page) => {
    if (!page) return false;
    const svg = String(page.svg || '');
    const aDuTexte = Boolean(page.court) || (Array.isArray(page.lignes) && page.lignes.length > 0);
    if (!svg) return aDuTexte;              // page de texte seul : rien ne bouge
    return !MOTIFS_NON_IMPRIMABLES.some((re) => re.test(svg));
};

/**
 * Pages d'une fiche retenues pour le papier, dans l'ordre de l'écran.
 *
 * Écart ASSUMÉ avec la feuille élève : le conseil générique de Tano (fiche
 * absente, ou réduite à une phrase passe-partout) ne produit AUCUNE carte.
 * « Relis bien l'énoncé » imprimé et découpé n'aiderait personne, et la
 * doctrine le classe déjà comme un non-coup-de-pouce.
 */
export const pagesImprimables = (fiche) => {
    if (!fiche || typeof fiche !== 'object') return [];

    let pages;
    if (Array.isArray(fiche.pages) && fiche.pages.length) {
        pages = fiche.pages.slice(0, 4);    // plafond de la feuille élève, respecté ici aussi
    } else if (fiche.court || fiche.svg) {
        // Forme courte historique { court, svg?, exemple? }.
        const ex = fiche.exemple;
        const lignes = Array.isArray(ex) ? ex : (ex ? [ex] : []);
        pages = [{ titre: 'La règle', court: fiche.court, svg: fiche.svg }];
        if (lignes.length) pages.push({ titre: 'Un exemple', lignes });
    } else {
        return [];
    }

    return pages.filter(pageImprimable);
};

/** Signature de CONTENU : deux objets distincts mais identiques = une aide. */
const signature = (pages) => JSON.stringify(
    pages.map((p) => [p?.titre, p?.court, p?.lignes, p?.svg]),
);

/**
 * Cartes à imprimer pour une liste de blocs paginés.
 *
 * Chaque carte porte les OBJETS blocs dont elle vient, jamais leurs numéros :
 * la numérotation définitive est posée par `renumberBlocks` APRÈS pagination.
 * C'est au rendu que le numéro se lit, d'où `libelleExercices` ci-dessous.
 * Le tableau `blocs` est partagé par référence entre les cartes d'une même
 * aide : un bloc découvert plus tard enrichit toute la série.
 *
 * @param {Array} blocks blocs issus de groupQuestions/packBlocks
 * @returns {Array<{id:number, page:Object, indexPage:number, nbPages:number, blocs:Array}>}
 */
export const construireCartesAide = (blocks = []) => {
    const cartes = [];
    const parSignature = new Map();

    for (const block of blocks) {
        for (const payload of payloadsDuBloc(block)) {
            const pages = pagesImprimables(getIndice(payload));
            if (!pages.length) continue;

            const sig = signature(pages);
            let serie = parSignature.get(sig);
            if (!serie) {
                serie = { blocs: [] };
                pages.forEach((page, indexPage) => {
                    cartes.push({
                        id: cartes.length,
                        page,
                        indexPage,
                        nbPages: pages.length,
                        blocs: serie.blocs,
                    });
                });
                parSignature.set(sig, serie);
            }
            if (!serie.blocs.includes(block)) serie.blocs.push(block);
        }
    }

    return cartes;
};

/**
 * « Exercice 3 » / « Exercices 2 et 5 » — lu AU RENDU, jamais au calcul.
 * Les numéros absents sont ignorés plutôt que rendus en « ? » : une carte sans
 * numéro lisible reste utile, le titre dit de quoi elle parle.
 */
export const libelleExercices = (blocs = []) => {
    const nums = [...new Set(blocs.map((b) => b?.exNum).filter((n) => Number.isFinite(n)))]
        .sort((a, b) => a - b);
    if (!nums.length) return 'Coup de pouce';
    if (nums.length === 1) return `Exercice ${nums[0]}`;
    const debut = nums.slice(0, -1).join(', ');
    return `Exercices ${debut} et ${nums[nums.length - 1]}`;
};
